import { useState, useEffect, useCallback } from 'react';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Globe,
  Sparkles,
  Video,
  Users,
  TrendingUp,
  Link as LinkIcon,
  Plus,
  ExternalLink,
  Settings,
  BarChart3,
  Target,
  Megaphone,
  Instagram,
  Facebook,
  Youtube,
  Music,
  Languages,
  Palette,
  Play,
  CheckCircle,
  Clock,
  Euro,
  Zap,
  ShoppingCart,
  Store,
  Rocket,
  Wand2,
  Bot,
  Share2,
  XCircle,
  Bell,
  BellRing,
  Check,
  Unlink,
  Send,
  RefreshCw,
  Eye,
  Edit3,
} from 'lucide-react';
import { toast } from 'sonner';

// Tarification du module
const PRICING = {
  starter: {
    name: 'Starter',
    price: 29,
    features: ['1 site web', '5 vidéos IA/mois', 'Statistiques basiques'],
    videoCredits: 5,
  },
  pro: {
    name: 'Professionnel',
    price: 79,
    features: ['Sites illimités', '25 vidéos IA/mois', 'Statistiques avancées', 'Multi-réseaux'],
    videoCredits: 25,
  },
  enterprise: {
    name: 'Entreprise',
    price: 199,
    features: ['Tout illimité', 'Vidéos IA illimitées', 'Support prioritaire', 'API accès'],
    videoCredits: -1,
  },
};

// Réseaux sociaux disponibles
const SOCIAL_NETWORKS = [
  { id: 'instagram', name: 'Instagram', icon: Instagram, color: '#E4405F' },
  { id: 'facebook', name: 'Facebook', icon: Facebook, color: '#1877F2' },
  { id: 'youtube', name: 'YouTube', icon: Youtube, color: '#FF0000' },
  { id: 'tiktok', name: 'TikTok', icon: Music, color: '#000000' },
];

// Voix disponibles pour la génération vidéo
const VOICES = [
  { id: 'nova', name: 'Nova', gender: 'Féminin', style: 'Professionnelle' },
  { id: 'alloy', name: 'Alloy', gender: 'Neutre', style: 'Moderne' },
  { id: 'echo', name: 'Echo', gender: 'Masculin', style: 'Chaleureux' },
  { id: 'fable', name: 'Fable', gender: 'Masculin', style: 'Narratif' },
  { id: 'onyx', name: 'Onyx', gender: 'Masculin', style: 'Autoritaire' },
  { id: 'shimmer', name: 'Shimmer', gender: 'Féminin', style: 'Dynamique' },
];

// Langues disponibles
const LANGUAGES = [
  { id: 'fr', name: 'Français', flag: '🇫🇷' },
  { id: 'en', name: 'Anglais', flag: '🇬🇧' },
  { id: 'es', name: 'Espagnol', flag: '🇪🇸' },
  { id: 'de', name: 'Allemand', flag: '🇩🇪' },
  { id: 'it', name: 'Italien', flag: '🇮🇹' },
];

// Styles musicaux
const MUSIC_STYLES = [
  { id: 'corporate', name: 'Corporate', description: 'Professionnel et moderne' },
  { id: 'upbeat', name: 'Dynamique', description: 'Énergique et motivant' },
  { id: 'nature', name: 'Nature', description: 'Sons naturels, relaxant' },
  { id: 'electronic', name: 'Électronique', description: 'Moderne et tech' },
  { id: 'acoustic', name: 'Acoustique', description: 'Chaleureux et authentique' },
  { id: 'none', name: 'Sans musique', description: 'Voix uniquement' },
];

// Cibles marketing
const TARGET_AUDIENCES = [
  { id: 'b2b', name: 'B2B', description: 'Professionnels et entreprises' },
  { id: 'b2c', name: 'B2C', description: 'Grand public' },
  { id: 'local', name: 'Local', description: 'Clientèle de proximité' },
  { id: 'export', name: 'Export', description: 'Marchés internationaux' },
];

// Component: Centre de Notifications
const NotificationCenter = () => {
  const { activeSociete } = useWorkspace();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchNotifications = useCallback(async () => {
    if (!activeSociete?.id) return;
    try {
      const response = await fetch(`${API_URL}/api/ecommerce/notifications?societe_id=${activeSociete.id}`);
      const data = await response.json();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (error) {
      console.error('Erreur chargement notifications:', error);
    }
  }, [activeSociete?.id, API_URL]);

  useEffect(() => {
    fetchNotifications();
    // Polling toutes les 10 secondes
    const interval = setInterval(fetchNotifications, 10000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleMarkRead = async (notificationId) => {
    try {
      await fetch(`${API_URL}/api/ecommerce/notifications/${notificationId}/read?societe_id=${activeSociete?.id}`, {
        method: 'PATCH'
      });
      fetchNotifications();
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleAction = (action, notification) => {
    if (action.action === 'view') {
      toast.info('Ouverture de la vidéo...');
      // TODO: Ouvrir la modal de preview
    } else if (action.action === 'publish') {
      toast.info('Ouverture du panneau de publication...');
    } else if (action.action === 'edit') {
      toast.info('Modification avec l\'IA...');
    } else if (action.action === 'retry') {
      toast.info('Relance de la génération...');
    }
    handleMarkRead(notification.id);
  };

  // Demander la permission pour les notifications du navigateur
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Afficher une notification du navigateur quand une nouvelle arrive
  useEffect(() => {
    if (unreadCount > 0 && 'Notification' in window && Notification.permission === 'granted') {
      const latestUnread = notifications.find(n => !n.read);
      if (latestUnread) {
        new Notification(latestUnread.title, {
          body: latestUnread.message,
          icon: '/favicon.ico'
        });
      }
    }
  }, [unreadCount, notifications]);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" className="relative" data-testid="notification-bell">
          {unreadCount > 0 ? (
            <BellRing className="w-4 h-4 text-primary animate-pulse" />
          ) : (
            <Bell className="w-4 h-4" />
          )}
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <div className="p-3 border-b flex items-center justify-between">
          <h4 className="font-semibold">Notifications</h4>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={async () => {
                await fetch(`${API_URL}/api/ecommerce/notifications/mark-all-read?societe_id=${activeSociete?.id}`, {
                  method: 'POST'
                });
                fetchNotifications();
              }}
            >
              Tout marquer comme lu
            </Button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground">
              <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Aucune notification</p>
            </div>
          ) : (
            notifications.map(notification => (
              <div 
                key={notification.id}
                className={`p-3 border-b last:border-0 ${!notification.read ? 'bg-primary/5' : ''}`}
              >
                <div className="flex gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    notification.type === 'video_ready' ? 'bg-green-100 text-green-600' :
                    notification.type === 'video_error' ? 'bg-red-100 text-red-600' :
                    'bg-blue-100 text-blue-600'
                  }`}>
                    {notification.type === 'video_ready' ? <CheckCircle className="w-4 h-4" /> :
                     notification.type === 'video_error' ? <XCircle className="w-4 h-4" /> :
                     <Bell className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{notification.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{notification.message}</p>
                    {notification.actions && (
                      <div className="flex gap-2 mt-2">
                        {notification.actions.map((action, idx) => (
                          <Button
                            key={idx}
                            variant={idx === 0 ? 'default' : 'outline'}
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => handleAction(action, notification)}
                          >
                            {action.action === 'view' && <Eye className="w-3 h-3 mr-1" />}
                            {action.action === 'publish' && <Send className="w-3 h-3 mr-1" />}
                            {action.action === 'edit' && <Edit3 className="w-3 h-3 mr-1" />}
                            {action.action === 'retry' && <RefreshCw className="w-3 h-3 mr-1" />}
                            {action.label}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                  {!notification.read && (
                    <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-2" />
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Component: Connexions Réseaux Sociaux
const SocialConnectionsSection = () => {
  const { activeSociete } = useWorkspace();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchConnections = useCallback(async () => {
    if (!activeSociete?.id) return;
    try {
      const response = await fetch(`${API_URL}/api/social/connections?societe_id=${activeSociete.id}`);
      const data = await response.json();
      setConnections(data.connections || []);
    } catch (error) {
      console.error('Erreur chargement connexions:', error);
    } finally {
      setLoading(false);
    }
  }, [activeSociete?.id, API_URL]);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  const handleConnect = async (platform) => {
    if (!activeSociete?.id) {
      toast.error('Veuillez sélectionner une société');
      return;
    }

    setConnecting(platform);
    try {
      const response = await fetch(
        `${API_URL}/api/social/connect/${platform}?societe_id=${activeSociete.id}&redirect_uri=${window.location.origin}/ecommerce`
      );
      const data = await response.json();
      
      if (data.success) {
        toast.success(`Compte ${SOCIAL_NETWORKS.find(n => n.id === platform)?.name} connecté !`);
        fetchConnections();
      } else {
        toast.error('Erreur de connexion');
      }
    } catch (error) {
      console.error('Erreur connexion:', error);
      toast.error('Erreur de connexion');
    } finally {
      setConnecting(null);
    }
  };

  const handleDisconnect = async (connectionId, platformName) => {
    try {
      await fetch(`${API_URL}/api/social/disconnect/${connectionId}?societe_id=${activeSociete?.id}`, {
        method: 'DELETE'
      });
      toast.success(`Compte ${platformName} déconnecté`);
      fetchConnections();
    } catch (error) {
      console.error('Erreur déconnexion:', error);
      toast.error('Erreur de déconnexion');
    }
  };

  const isConnected = (platform) => connections.some(c => c.platform === platform);
  const getConnection = (platform) => connections.find(c => c.platform === platform);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Share2 className="w-5 h-5" />
          Réseaux Sociaux Connectés
        </h3>
        <p className="text-sm text-muted-foreground">
          Connectez vos comptes pour publier automatiquement vos vidéos
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {SOCIAL_NETWORKS.map(network => {
          const Icon = network.icon;
          const connected = isConnected(network.id);
          const connection = getConnection(network.id);
          
          return (
            <Card 
              key={network.id} 
              className={`transition-all ${connected ? 'border-green-500/50 bg-green-50/50 dark:bg-green-950/20' : ''}`}
            >
              <CardContent className="p-4">
                <div className="flex flex-col items-center text-center gap-2">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${network.color}20` }}
                  >
                    <Icon className="w-5 h-5" style={{ color: network.color }} />
                  </div>
                  <p className="font-medium text-sm">{network.name}</p>
                  
                  {connected ? (
                    <div className="space-y-2 w-full">
                      <Badge variant="outline" className="text-green-600 border-green-300 gap-1">
                        <Check className="w-3 h-3" />
                        Connecté
                      </Badge>
                      <p className="text-xs text-muted-foreground truncate">
                        {connection?.account_name}
                      </p>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="w-full text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDisconnect(connection.id, network.name)}
                      >
                        <Unlink className="w-3 h-3 mr-1" />
                        Déconnecter
                      </Button>
                    </div>
                  ) : (
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="w-full"
                      onClick={() => handleConnect(network.id)}
                      disabled={connecting === network.id}
                    >
                      {connecting === network.id ? (
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-1" />
                      ) : (
                        <Plus className="w-3 h-3 mr-1" />
                      )}
                      Connecter
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      
      <div className="p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
        <p className="flex items-center gap-2">
          <LinkIcon className="w-4 h-4" />
          Une fois connectés, vos réseaux apparaîtront dans le générateur de vidéos pour publication directe.
        </p>
      </div>
    </div>
  );
};

// Component: Site Web Existant / Nouveau
const WebsiteSection = () => {
  const [hasExistingSite, setHasExistingSite] = useState(null);
  const [siteUrl, setSiteUrl] = useState('');
  const [isLinking, setIsLinking] = useState(false);

  const handleLinkSite = async () => {
    if (!siteUrl) {
      toast.error('Veuillez entrer l\'URL de votre site');
      return;
    }
    setIsLinking(true);
    // Simulation de la liaison
    await new Promise(resolve => setTimeout(resolve, 1500));
    toast.success('Site web lié avec succès !');
    setIsLinking(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Site Internet</h3>
          <p className="text-sm text-muted-foreground">Connectez ou créez votre vitrine en ligne</p>
        </div>
        <Badge variant="outline" className="gap-1">
          <Globe className="w-3 h-3" />
          Web
        </Badge>
      </div>

      {hasExistingSite === null && (
        <div className="grid md:grid-cols-2 gap-4">
          <Card 
            className="cursor-pointer hover:border-primary transition-colors"
            onClick={() => setHasExistingSite(true)}
            data-testid="link-existing-site"
          >
            <CardContent className="p-6 flex flex-col items-center text-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <LinkIcon className="w-6 h-6 text-primary" />
              </div>
              <h4 className="font-semibold">J'ai déjà un site</h4>
              <p className="text-sm text-muted-foreground">
                Liez votre site existant pour synchroniser les données
              </p>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:border-primary transition-colors"
            onClick={() => setHasExistingSite(false)}
            data-testid="create-new-site"
          >
            <CardContent className="p-6 flex flex-col items-center text-center gap-3">
              <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center">
                <Plus className="w-6 h-6 text-amber-500" />
              </div>
              <h4 className="font-semibold">Créer un nouveau site</h4>
              <p className="text-sm text-muted-foreground">
                Utilisez notre constructeur de site intégré
              </p>
              <Badge className="bg-amber-500">Bientôt disponible</Badge>
            </CardContent>
          </Card>
        </div>
      )}

      {hasExistingSite === true && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <LinkIcon className="w-4 h-4" />
              Lier mon site existant
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="site-url">URL du site</Label>
              <div className="flex gap-2">
                <Input
                  id="site-url"
                  placeholder="https://www.mon-exploitation.fr"
                  value={siteUrl}
                  onChange={(e) => setSiteUrl(e.target.value)}
                  data-testid="site-url-input"
                />
                <Button 
                  onClick={handleLinkSite} 
                  disabled={isLinking}
                  data-testid="link-site-button"
                >
                  {isLinking ? 'Liaison...' : 'Lier'}
                </Button>
              </div>
            </div>
            <div className="p-4 bg-muted rounded-lg">
              <h5 className="font-medium mb-2">Données synchronisées :</h5>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Formulaires de contact → CRM
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Statistiques de visite
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Leads capturés → Pipeline commercial
                </li>
              </ul>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setHasExistingSite(null)}
            >
              ← Retour
            </Button>
          </CardContent>
        </Card>
      )}

      {hasExistingSite === false && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Rocket className="w-4 h-4" />
              Constructeur de site
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-8 text-center bg-gradient-to-br from-primary/5 to-amber-500/5 rounded-lg border border-dashed">
              <Rocket className="w-12 h-12 mx-auto text-amber-500 mb-4" />
              <h4 className="font-semibold mb-2">Bientôt disponible</h4>
              <p className="text-sm text-muted-foreground mb-4">
                Notre constructeur de site vous permettra de créer une vitrine professionnelle en quelques clics.
              </p>
              <Button variant="outline" disabled>
                <Clock className="w-4 h-4 mr-2" />
                Être notifié du lancement
              </Button>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setHasExistingSite(null)}
            >
              ← Retour
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Component: Agent IA Influenceur
const AIInfluencerSection = () => {
  const { activeSociete } = useWorkspace();
  const [showCreateVideo, setShowCreateVideo] = useState(false);
  const [videoConfig, setVideoConfig] = useState({
    title: '',
    description: '',
    voice: 'nova',
    language: 'fr',
    music: 'corporate',
    target: 'b2c',
    networks: [],
    includeLeadForm: true,
    leadFormFields: ['name', 'email', 'phone'],
    // Sora 2 params
    videoSize: '1280x720',
    videoDuration: 4,
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedVideos, setGeneratedVideos] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Charger les vidéos existantes
  useEffect(() => {
    const fetchVideos = async () => {
      if (!activeSociete?.id) return;
      try {
        const response = await fetch(`${API_URL}/api/ecommerce/videos?societe_id=${activeSociete.id}`);
        const data = await response.json();
        setGeneratedVideos(data.videos || []);
      } catch (error) {
        console.error('Erreur chargement vidéos:', error);
      } finally {
        setLoadingVideos(false);
      }
    };
    fetchVideos();
  }, [activeSociete?.id, API_URL]);

  // Polling pour les vidéos en cours de génération
  useEffect(() => {
    const generatingVideos = generatedVideos.filter(v => v.status === 'queued' || v.status === 'generating');
    if (generatingVideos.length === 0) return;

    const interval = setInterval(async () => {
      for (const video of generatingVideos) {
        try {
          const response = await fetch(
            `${API_URL}/api/ecommerce/videos/${video.id}/status?societe_id=${activeSociete?.id}`
          );
          const status = await response.json();
          
          if (status.status !== video.status) {
            setGeneratedVideos(prev => prev.map(v => 
              v.id === video.id ? { ...v, ...status } : v
            ));
            
            if (status.status === 'draft') {
              toast.success(`Vidéo "${video.title}" générée avec succès !`);
            } else if (status.status === 'error') {
              toast.error(`Erreur génération: ${status.error_message}`);
            }
          }
        } catch (error) {
          console.error('Erreur polling status:', error);
        }
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [generatedVideos, activeSociete?.id, API_URL]);

  const handleGenerateVideo = async () => {
    if (!videoConfig.title) {
      toast.error('Veuillez entrer un titre pour la vidéo');
      return;
    }
    if (!activeSociete?.id) {
      toast.error('Veuillez sélectionner une société');
      return;
    }

    setIsGenerating(true);
    try {
      const response = await fetch(`${API_URL}/api/ecommerce/videos/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: videoConfig.title,
          description: videoConfig.description,
          voice: videoConfig.voice,
          language: videoConfig.language,
          music_style: videoConfig.music,
          target_audience: videoConfig.target,
          networks: videoConfig.networks,
          include_lead_form: videoConfig.includeLeadForm,
          lead_form_fields: videoConfig.leadFormFields,
          societe_id: activeSociete.id,
          video_size: videoConfig.videoSize,
          video_duration: videoConfig.videoDuration,
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        setGeneratedVideos(prev => [{
          id: data.video.id,
          title: data.video.title,
          status: 'queued',
          views: 0,
          leads: 0,
          networks: videoConfig.networks,
          createdAt: new Date().toISOString().split('T')[0],
          generation_progress: 0,
        }, ...prev]);
        
        toast.success('Génération Sora 2 lancée ! La vidéo sera prête dans 2-5 minutes.');
        setShowCreateVideo(false);
        setVideoConfig({
          title: '', description: '', voice: 'nova', language: 'fr',
          music: 'corporate', target: 'b2c', networks: [],
          includeLeadForm: true, leadFormFields: ['name', 'email', 'phone'],
          videoSize: '1280x720', videoDuration: 4,
        });
      } else {
        toast.error('Erreur lors de la création');
      }
    } catch (error) {
      console.error('Erreur génération:', error);
      toast.error('Erreur de connexion au serveur');
    } finally {
      setIsGenerating(false);
    }
  };

  const toggleNetwork = (networkId) => {
    setVideoConfig(prev => ({
      ...prev,
      networks: prev.networks.includes(networkId)
        ? prev.networks.filter(n => n !== networkId)
        : [...prev.networks, networkId]
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Bot className="w-5 h-5 text-purple-500" />
            Agent IA Influenceur
          </h3>
          <p className="text-sm text-muted-foreground">
            Générez des vidéos promotionnelles avec l'IA et publiez-les automatiquement
          </p>
        </div>
        <Dialog open={showCreateVideo} onOpenChange={setShowCreateVideo}>
          <DialogTrigger asChild>
            <Button data-testid="create-ai-video-button">
              <Wand2 className="w-4 h-4 mr-2" />
              Nouvelle vidéo IA
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                Créer une vidéo IA
              </DialogTitle>
              <DialogDescription>
                Configurez votre vidéo promotionnelle générée par l'IA
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Titre et description */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="video-title">Titre de la vidéo *</Label>
                  <Input
                    id="video-title"
                    placeholder="Ex: Découvrez nos produits bio"
                    value={videoConfig.title}
                    onChange={(e) => setVideoConfig(prev => ({ ...prev, title: e.target.value }))}
                    data-testid="video-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="video-description">Description / Script (optionnel)</Label>
                  <Textarea
                    id="video-description"
                    placeholder="Décrivez le contenu souhaité ou laissez l'IA générer le script..."
                    value={videoConfig.description}
                    onChange={(e) => setVideoConfig(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    data-testid="video-description-input"
                  />
                </div>
              </div>

              {/* Voix et Langue */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Music className="w-4 h-4" />
                    Voix
                  </Label>
                  <Select 
                    value={videoConfig.voice} 
                    onValueChange={(val) => setVideoConfig(prev => ({ ...prev, voice: val }))}
                  >
                    <SelectTrigger data-testid="voice-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {VOICES.map(voice => (
                        <SelectItem key={voice.id} value={voice.id}>
                          <span className="flex items-center gap-2">
                            {voice.name}
                            <span className="text-xs text-muted-foreground">
                              ({voice.gender} - {voice.style})
                            </span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Languages className="w-4 h-4" />
                    Langue
                  </Label>
                  <Select 
                    value={videoConfig.language} 
                    onValueChange={(val) => setVideoConfig(prev => ({ ...prev, language: val }))}
                  >
                    <SelectTrigger data-testid="language-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LANGUAGES.map(lang => (
                        <SelectItem key={lang.id} value={lang.id}>
                          <span className="flex items-center gap-2">
                            <span>{lang.flag}</span>
                            {lang.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Musique */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Palette className="w-4 h-4" />
                  Style musical
                </Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {MUSIC_STYLES.map(style => (
                    <button
                      key={style.id}
                      onClick={() => setVideoConfig(prev => ({ ...prev, music: style.id }))}
                      className={`p-3 rounded-lg border text-left transition-colors ${
                        videoConfig.music === style.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-muted-foreground'
                      }`}
                      data-testid={`music-style-${style.id}`}
                    >
                      <p className="font-medium text-sm">{style.name}</p>
                      <p className="text-xs text-muted-foreground">{style.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Cible */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Cible marketing
                </Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {TARGET_AUDIENCES.map(target => (
                    <button
                      key={target.id}
                      onClick={() => setVideoConfig(prev => ({ ...prev, target: target.id }))}
                      className={`p-3 rounded-lg border text-center transition-colors ${
                        videoConfig.target === target.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-muted-foreground'
                      }`}
                      data-testid={`target-${target.id}`}
                    >
                      <p className="font-medium text-sm">{target.name}</p>
                      <p className="text-xs text-muted-foreground">{target.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Réseaux sociaux */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Share2 className="w-4 h-4" />
                  Publier sur
                </Label>
                <div className="flex flex-wrap gap-2">
                  {SOCIAL_NETWORKS.map(network => {
                    const Icon = network.icon;
                    const isSelected = videoConfig.networks.includes(network.id);
                    return (
                      <button
                        key={network.id}
                        onClick={() => toggleNetwork(network.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-colors ${
                          isSelected
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-muted-foreground'
                        }`}
                        data-testid={`network-${network.id}`}
                      >
                        <Icon className="w-4 h-4" style={{ color: isSelected ? network.color : undefined }} />
                        <span className="text-sm">{network.name}</span>
                        {isSelected && <CheckCircle className="w-4 h-4 text-primary" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Paramètres Sora 2 */}
              <div className="p-4 bg-purple-500/5 border border-purple-500/20 rounded-lg space-y-4">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-500" />
                  <Label className="font-semibold text-purple-700 dark:text-purple-300">Paramètres Sora 2</Label>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* Taille vidéo */}
                  <div className="space-y-2">
                    <Label className="text-sm">Format vidéo</Label>
                    <Select 
                      value={videoConfig.videoSize} 
                      onValueChange={(val) => setVideoConfig(prev => ({ ...prev, videoSize: val }))}
                    >
                      <SelectTrigger data-testid="video-size-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1280x720">
                          <span className="flex items-center gap-2">
                            <span className="text-xs bg-muted px-1 rounded">HD</span>
                            1280×720 (16:9)
                          </span>
                        </SelectItem>
                        <SelectItem value="1792x1024">
                          <span className="flex items-center gap-2">
                            <span className="text-xs bg-muted px-1 rounded">Wide</span>
                            1792×1024 (Paysage)
                          </span>
                        </SelectItem>
                        <SelectItem value="1024x1792">
                          <span className="flex items-center gap-2">
                            <span className="text-xs bg-muted px-1 rounded">Story</span>
                            1024×1792 (Portrait)
                          </span>
                        </SelectItem>
                        <SelectItem value="1024x1024">
                          <span className="flex items-center gap-2">
                            <span className="text-xs bg-muted px-1 rounded">1:1</span>
                            1024×1024 (Carré)
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Durée */}
                  <div className="space-y-2">
                    <Label className="text-sm">Durée</Label>
                    <Select 
                      value={videoConfig.videoDuration.toString()} 
                      onValueChange={(val) => setVideoConfig(prev => ({ ...prev, videoDuration: parseInt(val) }))}
                    >
                      <SelectTrigger data-testid="video-duration-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="4">4 secondes (rapide)</SelectItem>
                        <SelectItem value="8">8 secondes</SelectItem>
                        <SelectItem value="12">12 secondes (long)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <p className="text-xs text-muted-foreground">
                  La génération prend environ 2-5 minutes selon la durée choisie
                </p>
              </div>

              {/* Formulaire de capture de leads */}
              <div className="p-4 bg-muted rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Formulaire de capture de leads
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Intégrer un formulaire dans la vidéo pour capturer des prospects
                    </p>
                  </div>
                  <Switch
                    checked={videoConfig.includeLeadForm}
                    onCheckedChange={(val) => setVideoConfig(prev => ({ ...prev, includeLeadForm: val }))}
                    data-testid="lead-form-switch"
                  />
                </div>
                {videoConfig.includeLeadForm && (
                  <div className="flex flex-wrap gap-2 pt-2">
                    {['name', 'email', 'phone', 'company', 'message'].map(field => {
                      const fieldLabels = {
                        name: 'Nom',
                        email: 'Email',
                        phone: 'Téléphone',
                        company: 'Entreprise',
                        message: 'Message',
                      };
                      const isSelected = videoConfig.leadFormFields.includes(field);
                      return (
                        <Badge
                          key={field}
                          variant={isSelected ? 'default' : 'outline'}
                          className="cursor-pointer"
                          onClick={() => {
                            setVideoConfig(prev => ({
                              ...prev,
                              leadFormFields: isSelected
                                ? prev.leadFormFields.filter(f => f !== field)
                                : [...prev.leadFormFields, field]
                            }));
                          }}
                        >
                          {fieldLabels[field]}
                        </Badge>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateVideo(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleGenerateVideo} 
                disabled={isGenerating}
                data-testid="generate-video-button"
              >
                {isGenerating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Génération en cours...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Générer la vidéo
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Liste des vidéos générées */}
      <div className="grid gap-4">
        {loadingVideos ? (
          <Card className="p-8 text-center">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm text-muted-foreground">Chargement des vidéos...</p>
          </Card>
        ) : generatedVideos.length === 0 ? (
          <Card className="p-8 text-center">
            <Video className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h4 className="font-medium mb-2">Aucune vidéo générée</h4>
            <p className="text-sm text-muted-foreground mb-4">
              Créez votre première vidéo promotionnelle avec Sora 2
            </p>
            <Button onClick={() => setShowCreateVideo(true)}>
              <Wand2 className="w-4 h-4 mr-2" />
              Créer une vidéo
            </Button>
          </Card>
        ) : (
          generatedVideos.map(video => (
            <Card key={video.id} data-testid={`video-card-${video.id}`} className={
              video.status === 'queued' || video.status === 'generating' 
                ? 'border-purple-500/50 bg-purple-500/5' 
                : video.status === 'error' 
                  ? 'border-red-500/50 bg-red-500/5'
                  : ''
            }>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  {/* Thumbnail / Preview */}
                  <div className="w-24 h-16 bg-muted rounded-lg flex items-center justify-center relative overflow-hidden">
                    {video.status === 'queued' || video.status === 'generating' ? (
                      <div className="flex flex-col items-center">
                        <div className="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                        <span className="text-[10px] text-purple-600 mt-1">
                          {video.generation_progress || 0}%
                        </span>
                      </div>
                    ) : video.status === 'error' ? (
                      <XCircle className="w-8 h-8 text-red-500" />
                    ) : video.video_url ? (
                      <video 
                        src={`${API_URL}${video.video_url}`} 
                        className="w-full h-full object-cover"
                        muted
                      />
                    ) : (
                      <Play className="w-8 h-8 text-muted-foreground" />
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium truncate">{video.title}</h4>
                      {video.status === 'queued' && (
                        <Badge variant="outline" className="text-purple-600 border-purple-300">
                          <Clock className="w-3 h-3 mr-1" />
                          En attente
                        </Badge>
                      )}
                      {video.status === 'generating' && (
                        <Badge className="bg-purple-500">
                          <Sparkles className="w-3 h-3 mr-1" />
                          Génération Sora 2
                        </Badge>
                      )}
                      {video.status === 'draft' && (
                        <Badge variant="secondary">Brouillon</Badge>
                      )}
                      {video.status === 'published' && (
                        <Badge variant="default">Publié</Badge>
                      )}
                      {video.status === 'error' && (
                        <Badge variant="destructive">Erreur</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {video.created_at ? new Date(video.created_at).toLocaleDateString('fr-FR') : video.createdAt}
                    </p>
                    {video.error_message && (
                      <p className="text-xs text-red-600 mt-1">{video.error_message}</p>
                    )}
                    {video.networks && video.networks.length > 0 && (
                      <div className="flex gap-1 mt-2">
                        {video.networks.map(networkId => {
                          const network = SOCIAL_NETWORKS.find(n => n.id === networkId);
                          if (!network) return null;
                          const Icon = network.icon;
                          return (
                            <Icon 
                              key={networkId} 
                              className="w-4 h-4" 
                              style={{ color: network.color }} 
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>
                  
                  {/* Stats */}
                  {video.status !== 'queued' && video.status !== 'generating' && video.status !== 'error' && (
                    <div className="text-right">
                      <div className="flex items-center gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Vues</p>
                          <p className="font-semibold">{(video.stats?.views || video.views || 0).toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Leads</p>
                          <p className="font-semibold text-green-600">{video.stats?.leads || video.leads || 0}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  <Button variant="ghost" size="icon">
                    <Settings className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

// Component: Statistiques
const StatsSection = () => {
  const stats = [
    { label: 'Visites site', value: '3,247', change: '+12%', icon: Globe },
    { label: 'Leads générés', value: '48', change: '+23%', icon: Users },
    { label: 'Vidéos publiées', value: '7', change: '+2', icon: Video },
    { label: 'Engagement', value: '4.2%', change: '+0.8%', icon: TrendingUp },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold">Statistiques Marketing</h3>
        <p className="text-sm text-muted-foreground">Vue d'ensemble de vos performances</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map(stat => (
          <Card key={stat.label}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <stat.icon className="w-5 h-5 text-muted-foreground" />
                <Badge variant="outline" className="text-green-600 text-xs">
                  {stat.change}
                </Badge>
              </div>
              <p className="text-2xl font-bold">{stat.value}</p>
              <p className="text-xs text-muted-foreground">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Performance des 30 derniers jours</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-48 flex items-center justify-center bg-muted rounded-lg">
            <div className="text-center">
              <BarChart3 className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">Graphique de performance</p>
              <p className="text-xs text-muted-foreground">(En cours de développement)</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Component: Tarification
const PricingSection = () => {
  const [selectedPlan, setSelectedPlan] = useState('starter');

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold">Tarification</h3>
        <p className="text-sm text-muted-foreground">Choisissez le plan adapté à vos besoins</p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {Object.entries(PRICING).map(([key, plan]) => (
          <Card 
            key={key}
            className={`cursor-pointer transition-all ${
              selectedPlan === key ? 'border-primary ring-2 ring-primary/20' : ''
            }`}
            onClick={() => setSelectedPlan(key)}
            data-testid={`plan-${key}`}
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{plan.name}</CardTitle>
                {key === 'pro' && (
                  <Badge className="bg-amber-500">Populaire</Badge>
                )}
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-bold">{plan.price}</span>
                <span className="text-muted-foreground">€/mois</span>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
              <Button 
                className="w-full mt-4"
                variant={selectedPlan === key ? 'default' : 'outline'}
              >
                {selectedPlan === key ? 'Plan sélectionné' : 'Choisir'}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Page Principale E-commerce
export default function EcommercePage() {
  return (
    <div className="space-y-6" data-testid="ecommerce-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Store className="w-7 h-7 text-purple-500" />
            E-commerce & Marketing
          </h1>
          <p className="text-muted-foreground">
            Gérez votre présence en ligne et vos campagnes marketing IA
          </p>
        </div>
        <div className="flex items-center gap-2">
          <NotificationCenter />
          <Badge variant="outline" className="gap-1">
            <Zap className="w-3 h-3 text-amber-500" />
            Module Premium
          </Badge>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="website" className="space-y-6">
        <TabsList className="grid grid-cols-5 w-full max-w-3xl">
          <TabsTrigger value="website" className="gap-2" data-testid="tab-website">
            <Globe className="w-4 h-4" />
            <span className="hidden sm:inline">Site</span>
          </TabsTrigger>
          <TabsTrigger value="ai-influencer" className="gap-2" data-testid="tab-ai-influencer">
            <Bot className="w-4 h-4" />
            <span className="hidden sm:inline">IA Vidéo</span>
          </TabsTrigger>
          <TabsTrigger value="social" className="gap-2" data-testid="tab-social">
            <Share2 className="w-4 h-4" />
            <span className="hidden sm:inline">Réseaux</span>
          </TabsTrigger>
          <TabsTrigger value="stats" className="gap-2" data-testid="tab-stats">
            <BarChart3 className="w-4 h-4" />
            <span className="hidden sm:inline">Stats</span>
          </TabsTrigger>
          <TabsTrigger value="pricing" className="gap-2" data-testid="tab-pricing">
            <Euro className="w-4 h-4" />
            <span className="hidden sm:inline">Tarifs</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="website">
          <WebsiteSection />
        </TabsContent>

        <TabsContent value="ai-influencer">
          <AIInfluencerSection />
        </TabsContent>

        <TabsContent value="social">
          <SocialConnectionsSection />
        </TabsContent>

        <TabsContent value="stats">
          <StatsSection />
        </TabsContent>

        <TabsContent value="pricing">
          <PricingSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}
